import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import {
    Trophy,
    Target,
    Flame,
    BookOpen,
    TrendingUp,
    Clock,
    BarChart3,
    ArrowLeft,
    Check,
    Medal,
    Star,
    Zap,
    GraduationCap,
    AlertCircle
} from 'lucide-react';
import { BADGES } from '../../utils/badges';
import { Link } from 'react-router-dom';
import { encodeId } from '../../utils/ids';
import { getWrongQuestionsCount } from '../../utils/wrongQuestions';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    BarChart,
    Bar,
    Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Stats() {
    const { t, i18n } = useTranslation();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState<any[]>([]);
    const [userProgress, setUserProgress] = useState<any[]>([]);
    const [categoryCounts, setCategoryCounts] = useState<any>({});

    const loadUserProgress = useCallback(async (userId: string) => {
        try {
            const { data, error } = await supabase
                .from('user_progress')
                .select('*')
                .eq('user_id', userId);

            if (error) throw error;
            setUserProgress(data || []);
        } catch (error) {
            console.error('Error loading progress:', error);
        }
    }, []);

    const loadCategories = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('categories')
                .select('*')
                .order('id', { ascending: true });

            if (error) throw error;
            setCategories(data || []);

            // Calculate counts for each category
            const counts: any = {};
            for (const cat of data || []) {
                const [flashcards, qcm, vraiFaux] = await Promise.all([
                    supabase.from('flashcards').select('id', { count: 'exact', head: true }).eq('category_id', cat.id),
                    supabase.from('qcm').select('id', { count: 'exact', head: true }).eq('category_id', cat.id),
                    supabase.from('vrai_faux').select('id', { count: 'exact', head: true }).eq('category_id', cat.id)
                ]);

                counts[cat.id] = {
                    flashcards: flashcards.count || 0,
                    qcm: qcm.count || 0,
                    vrai_faux: vraiFaux.count || 0,
                    total: (flashcards.count || 0) + (qcm.count || 0) + (vraiFaux.count || 0)
                };
            }
            setCategoryCounts(counts);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    }, []);

    const checkAuth = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            window.REACT_APP_NAVIGATE('/login');
            return;
        }
        setUser(user);
        await Promise.all([
            loadCategories(),
            loadUserProgress(user.id)
        ]);
        setLoading(false);
    }, [loadCategories, loadUserProgress]);

    useEffect(() => {
        checkAuth();

        const { data: { subscription: authListener } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                if (session?.user) {
                    setUser(session.user);
                    if (event === 'USER_UPDATED') {
                        loadUserProgress(session.user.id);
                    }
                }
            }
        );

        return () => {
            authListener.unsubscribe();
        };
    }, [checkAuth, loadUserProgress]);

    const calculateGlobalStats = () => {
        const quizCompleted = userProgress.reduce((sum: number, p: any) =>
            sum + (p.qcm_completed || 0) + (p.vrai_faux_completed || 0), 0
        );
        const quizTotal = (Object.values(categoryCounts) as any[]).reduce((sum: number, c: any) =>
            sum + (c.qcm || 0) + (c.vrai_faux || 0), 0
        );

        const totalCompleted: number = userProgress.reduce((sum: number, p: any) =>
            sum + (p.flashcards_completed || 0) + (p.qcm_completed || 0) + (p.vrai_faux_completed || 0), 0
        );
        const totalCorrect: number = userProgress.reduce((sum: number, p: any) => sum + (p.total_score || 0), 0);

        const accuracy = totalCompleted > 0 ? Math.round((totalCorrect / totalCompleted) * 100) : 100;
        const completion = quizTotal > 0 ? Math.round((quizCompleted / quizTotal) * 100) : 0;

        return {
            totalCompleted,
            quizCompleted,
            quizTotal,
            accuracy,
            completion,
            streak: user?.user_metadata?.streak_count || 0,
            wrongCount: getWrongQuestionsCount(user)
        };
    };

    const stats = calculateGlobalStats();

    const chartData = categories.map(cat => {
        const progress = userProgress.find(p => p.category_id === cat.id) || {
            flashcards_completed: 0,
            qcm_completed: 0,
            vrai_faux_completed: 0,
            total_score: 0
        };
        const counts = categoryCounts[cat.id] || { qcm: 0, vrai_faux: 0, flashcards: 0, total: 1 };
        const quizCompleted = (progress.qcm_completed || 0) + (progress.vrai_faux_completed || 0);
        const quizTotal = (counts.qcm || 0) + (counts.vrai_faux || 0);
        const totalCompleted = quizCompleted + (progress.flashcards_completed || 0);
        const title = (i18n.language === 'en' ? cat.title_en : cat.title) || cat.title;

        return {
            name: cat.id,
            fullTitle: title,
            progress: quizTotal > 0 ? Math.min(Math.round((quizCompleted / quizTotal) * 100), 100) : 0,
            accuracy: totalCompleted > 0 ? Math.round((progress.total_score / totalCompleted) * 100) : 100
        };
    });

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-dark-card border border-dark-border p-4 rounded-xl shadow-xl z-50">
                    <p className="font-bold text-text-main mb-2">{payload[0].payload.fullTitle}</p>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: payload[0].color }} />
                        <span className="text-sm text-text-muted">
                            {payload[0].name === 'progress' ? t('stats.charts.legend_progress') : t('stats.charts.legend_accuracy')}:
                        </span>
                        <span className="text-sm font-bold text-text-main">
                            {payload[0].value}%
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <DashboardLayout user={user}>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout user={user}>
            <SEO noindex={true} />
            <div className="max-w-6xl mx-auto pb-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <div className="flex items-center gap-2 text-text-muted mb-2 hover:text-text-main transition-colors">
                            <Link to="/dashboard" className="flex items-center gap-1 text-sm font-medium">
                                <ArrowLeft size={16} />
                                {t('stats.nav.dashboard')}
                            </Link>
                        </div>
                        <h1 className="text-3xl font-bold text-text-main flex items-center gap-3">
                            <TrendingUp className="text-primary" size={32} />
                            {t('stats.title')}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3 bg-dark-card border border-dark-border px-4 py-2 rounded-xl">
                        <Clock className="text-text-muted" size={18} />
                        <span className="text-sm text-text-muted">{t('stats.last_update')}</span>
                    </div>
                </div>

                {/* Global Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {[
                        { label: t('stats.global.quiz'), value: `${stats.completion}%`, icon: Target, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                        { label: t('stats.global.accuracy'), value: `${stats.accuracy}%`, icon: Trophy, color: 'text-primary', bg: 'bg-primary/10' },
                        { label: t('stats.global.review'), value: stats.wrongCount, icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-500/10' },
                        { label: t('stats.global.streak'), value: `${stats.streak}${t('stats.global.streak_unit')}`, icon: Flame, color: 'text-orange-500', bg: 'bg-orange-500/10' },
                    ].map((item, i) => (
                        <div key={i} className="bg-dark-card p-6 rounded-2xl border border-dark-border flex items-center gap-4">
                            <div className={`p-4 rounded-xl ${item.bg} ${item.color}`}>
                                <item.icon size={24} />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-text-main">{item.value}</div>
                                <div className="text-sm text-text-muted">{item.label}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Badges Section */}
                <div className="mb-8 bg-dark-card rounded-2xl border border-dark-border p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-text-main flex items-center gap-2">
                            <Medal className="text-primary" size={24} />
                            {t('stats.badges.title')}
                        </h2>
                        <span className="text-xs font-bold text-text-muted bg-dark-bg px-3 py-1 rounded-full border border-dark-border">
                            {user?.user_metadata?.badges?.length || 0} {
                                (user?.user_metadata?.badges?.length || 0) > 1
                                    ? t('stats.badges.count_plural', { count: user?.user_metadata?.badges?.length })
                                    : t('stats.badges.count', { count: user?.user_metadata?.badges?.length || 0 })
                            }
                        </span>
                    </div>

                    <div className="flex flex-wrap gap-4">
                        {user?.user_metadata?.badges?.length > 0 ? (
                            user.user_metadata.badges.map((badge: any) => {
                                const staticBadge = BADGES.find(b => b.title === badge.title);
                                const imageSrc = badge.image || staticBadge?.image;
                                const title = (i18n.language === 'en' ? (badge.title_en || staticBadge?.title_en || badge.title) : (badge.title)) || badge.title;
                                const description = (i18n.language === 'en' ? (badge.description_en || staticBadge?.description_en || badge.description) : (badge.description)) || t('stats.badges.congrats');

                                return (
                                    <div
                                        key={badge.id}
                                        className="group relative flex flex-col items-center gap-2 p-4 bg-dark-bg/50 rounded-2xl border border-dark-border hover:border-primary/30 transition-all cursor-help"
                                    >
                                        {imageSrc ? (
                                            <div className="w-14 h-14 flex items-center justify-center group-hover:scale-110 transition-transform">
                                                <img src={imageSrc} alt={title} className="w-full h-full object-contain drop-shadow-md rounded-full" />
                                            </div>
                                        ) : (
                                            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                                {badge.icon === 'Trophy' && <Trophy size={28} />}
                                                {badge.icon === 'Star' && <Star size={28} />}
                                                {badge.icon === 'Zap' && <Zap size={28} />}
                                                {badge.icon === 'GraduationCap' && <GraduationCap size={28} />}
                                            </div>
                                        )}
                                        <span className="text-[10px] font-black uppercase tracking-widest text-text-muted group-hover:text-text-main transition-colors text-center">
                                            {title}
                                        </span>

                                        <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-48 bg-dark-card border border-dark-border p-2 rounded-lg text-[10px] text-text-muted opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-20 text-center shadow-2xl">
                                            {description}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="flex flex-col items-center justify-center w-full py-6 text-center border-2 border-dashed border-dark-border rounded-3xl bg-dark-bg/30">
                                <div className="w-16 h-16 bg-dark-card rounded-full flex items-center justify-center text-dark-border mb-3">
                                    <Medal size={32} />
                                </div>
                                <h4 className="text-sm font-bold text-text-main mb-1">{t('stats.badges.empty_title')}</h4>
                                <p className="text-xs text-text-muted max-w-xs mx-auto">
                                    {t('stats.badges.empty_desc')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Progress Chart */}
                    <div className="bg-dark-card p-6 rounded-2xl border border-dark-border">
                        <h3 className="text-lg font-bold text-text-main mb-6 flex items-center gap-2">
                            <BarChart3 className="text-primary" size={20} />
                            {t('stats.charts.progress_title')}
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                                        unit="%"
                                        domain={[0, 100]}
                                    />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(220, 38, 38, 0.05)' }} />
                                    <Bar dataKey="progress" radius={[4, 4, 0, 0]}>
                                        {chartData.map((_entry, index) => (
                                            <Cell key={`cell-${index}`} fill={'#DC2626'} fillOpacity={index === 0 ? 1 : 0.5} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Accuracy Area Chart */}
                    <div className="bg-dark-card p-6 rounded-2xl border border-dark-border">
                        <h3 className="text-lg font-bold text-text-main mb-6 flex items-center gap-2">
                            <TrendingUp className="text-primary" size={20} />
                            {t('stats.charts.accuracy_title')}
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorAcc" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#DC2626" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                                        unit="%"
                                        domain={[0, 100]}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="accuracy"
                                        stroke="#DC2626"
                                        fillOpacity={1}
                                        fill="url(#colorAcc)"
                                        strokeWidth={3}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Detailed List */}
                <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
                    <div className="p-6 border-b border-dark-border bg-dark-bg/50">
                        <h3 className="text-lg font-bold text-text-main">{t('stats.table.title')}</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="text-xs font-bold text-text-muted uppercase tracking-wider border-b border-dark-border">
                                    <th className="px-6 py-4">{t('stats.table.col_quiz')}</th>
                                    <th className="px-6 py-4">{t('stats.table.col_quiz')} Progression</th>
                                    <th className="px-6 py-4">{t('stats.table.col_flashcards')}</th>
                                    <th className="px-6 py-4">{t('stats.table.col_accuracy')}</th>
                                    <th className="px-6 py-4">{t('stats.table.col_challenges')}</th>
                                    <th className="px-6 py-4 text-right">{t('stats.table.col_action')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-dark-border">
                                {categories.map((cat) => {
                                    const progress = userProgress.find(p => p.category_id === cat.id) || {
                                        flashcards_completed: 0,
                                        qcm_completed: 0,
                                        vrai_faux_completed: 0,
                                        total_score: 0
                                    };
                                    const counts = categoryCounts[cat.id] || { qcm: 0, vrai_faux: 0, flashcards: 0, total: 1 };
                                    const quizCompleted = (progress.qcm_completed || 0) + (progress.vrai_faux_completed || 0);
                                    const quizTotal = (counts.qcm || 0) + (counts.vrai_faux || 0);
                                    const progressPercent = quizTotal > 0 ? Math.min(Math.round((quizCompleted / quizTotal) * 100), 100) : 0;
                                    const accuracyPercent = (quizCompleted + (progress.flashcards_completed || 0)) > 0
                                        ? Math.round((progress.total_score / (quizCompleted + (progress.flashcards_completed || 0))) * 100)
                                        : 100;
                                    const wrongCount = getWrongQuestionsCount(user, cat.id);
                                    const title = (i18n.language === 'en' ? cat.title_en : cat.title) || cat.title;

                                    return (
                                        <tr key={cat.id} className="hover:bg-dark-hover transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                                                        {cat.id}
                                                    </div>
                                                    <span className="font-medium text-text-main group-hover:text-primary transition-colors">{title}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3 w-32">
                                                    <div className="flex-1 h-1.5 bg-gray-200 dark:bg-dark-bg rounded-full overflow-hidden">
                                                        <div className="h-full bg-primary" style={{ width: `${progressPercent}%` }} />
                                                    </div>
                                                    <span className="text-xs font-bold text-text-main">{progressPercent}%</span>
                                                </div>
                                                <div className="text-[10px] text-text-muted mt-1">{t('stats.table.quiz_count', { completed: quizCompleted, total: quizTotal })}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-text-main font-medium">{progress.flashcards_completed || 0} / {counts.flashcards || 0}</span>
                                                <div className="text-[10px] text-text-muted mt-1">{t('stats.table.flashcards_desc')}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${accuracyPercent >= 80 ? 'bg-green-500' : accuracyPercent >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} />
                                                    <span className="text-sm font-medium text-text-main">{accuracyPercent}%</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {wrongCount > 0 ? (
                                                    <Link
                                                        to={`/quiz?category=${encodeId(cat.id)}&mode=retry`}
                                                        className="flex items-center gap-1.5 text-xs font-bold text-red-500 bg-red-500/10 px-2 py-1 rounded-lg border border-red-500/20 animate-pulse-subtle hover:bg-red-500/20 transition-all font-mono"
                                                    >
                                                        {t('stats.table.errors_count', { count: wrongCount })}
                                                    </Link>
                                                ) : (
                                                    <span className="text-xs text-text-muted flex items-center gap-1.5">
                                                        <Check size={12} className="text-green-500" /> {t('stats.table.perfect')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Link
                                                    to={`/quiz?category=${encodeId(cat.id)}`}
                                                    className="text-primary hover:text-white bg-primary/10 hover:bg-primary px-3 py-1 rounded-lg text-xs font-bold transition-all inline-block"
                                                >
                                                    {t('stats.table.review_btn')}
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </DashboardLayout >
    );
}
